import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { fetchUnlinkedEmails, fetchEmailDetail, linkEmailToCase, dismissUnlinkedEmail, dismissUnlinkedEmailsBulk, triggerOutlookSync, unlinkEmailFromCase, markEmailRead } from '../api';
import ComposeEmail from './ComposeEmail';

function UnlinkedInbox({ authAxios, showFormMessage, cases = [] }) {
  const [activeTab, setActiveTab] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [linkingId, setLinkingId] = useState(null);
  const [linkCaseId, setLinkCaseId] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCompose, setShowCompose] = useState(false);
  const [viewingEmail, setViewingEmail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const loadEmails = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { show_all: true, folder: activeTab };
      if (searchTerm) params.search = searchTerm;
      const data = await fetchUnlinkedEmails(authAxios, params);
      setEmails(data.results || data || []);
    } catch (err) {
      console.error('Error fetching emails:', err);
      showFormMessage('error', 'Could not load emails. Please check your connection and try again.');
      setEmails([]);
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, activeTab, searchTerm]);

  useEffect(() => { loadEmails(); }, [loadEmails]);

  const handleRefresh = async () => {
    setIsSyncing(true);
    try {
      await triggerOutlookSync(authAxios, 'email');
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setIsSyncing(false);
    }
    loadEmails();
  };

  const openLinkPanel = async (emailId) => {
    if (linkingId === emailId) {
      setLinkingId(null);
      setSuggestions([]);
      return;
    }
    setLinkingId(emailId);
    setLinkCaseId('');
    setSuggestions([]);
    try {
      const res = await authAxios.get(`/unlinked-emails/${emailId}/suggest-cases/`);
      setSuggestions(res.data || []);
    } catch {
      // Suggestions are optional — don't block linking
    }
  };

  const handleLink = async (emailId) => {
    if (!linkCaseId) {
      showFormMessage('error', 'Please select a case to link this email to.');
      return;
    }
    try {
      await linkEmailToCase(authAxios, emailId, linkCaseId);
      showFormMessage('success', 'Email linked to case.');
      setLinkingId(null);
      setLinkCaseId('');
      loadEmails();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to link email.');
    }
  };

  const handleUnlink = async (emailId) => {
    try {
      await unlinkEmailFromCase(authAxios, emailId);
      showFormMessage('success', 'Email unlinked from case.');
      loadEmails();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to unlink email.');
    }
  };

  const handleDismiss = async (emailId) => {
    try {
      await dismissUnlinkedEmail(authAxios, emailId);
      setEmails(prev => prev.filter(e => e.id !== emailId));
    } catch (err) {
      showFormMessage('error', 'Failed to dismiss email.');
    }
  };

  const handleBulkDismiss = async () => {
    if (selectedIds.size === 0) return;
    try {
      await dismissUnlinkedEmailsBulk(authAxios, Array.from(selectedIds));
      showFormMessage('success', `${selectedIds.size} emails dismissed.`);
      setSelectedIds(new Set());
      loadEmails();
    } catch (err) {
      showFormMessage('error', 'Failed to dismiss emails.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === emails.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(emails.map(e => e.id)));
    }
  };

  const handleViewEmail = async (email) => {
    setIsLoadingDetail(true);
    setViewingEmail({ ...email, body_html: null });
    // Mark as read immediately (optimistic update)
    if (!email.is_read && activeTab === 'inbox') {
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_read: true } : e));
      markEmailRead(authAxios, email.id).catch(() => {});
    }
    try {
      const detail = await fetchEmailDetail(authAxios, email.id);
      setViewingEmail(detail);
    } catch (err) {
      // Fall back to preview if full body fetch fails
      setViewingEmail({ ...email, body_html: email.body_preview, body_type: 'text' });
    } finally {
      setIsLoadingDetail(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Outlook Mail</h2>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm w-full sm:w-64"
          />
          <button
            onClick={handleRefresh}
            disabled={isSyncing}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isSyncing ? 'Syncing…' : 'Refresh'}
          </button>
          <button
            onClick={() => setShowCompose(true)}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
          >
            Compose
          </button>
        </div>
      </div>

      {/* Inbox / Sent tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {['inbox', 'sent'].map(tab => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setSelectedIds(new Set()); }}
            className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-blue-600 text-blue-700 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab === 'inbox' ? 'Inbox' : 'Sent'}
          </button>
        ))}
      </div>

      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 px-4 py-2 rounded mb-4 flex items-center gap-3">
          <span className="text-sm text-blue-800">{selectedIds.size} selected</span>
          <button
            onClick={handleBulkDismiss}
            className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
          >
            Dismiss Selected
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="text-gray-500 text-center py-12">Loading emails...</div>
      ) : emails.length === 0 ? (
        <div className="text-gray-500 text-center py-12">
          <p className="text-lg mb-2">No emails found</p>
          <p className="text-sm">Click Refresh to sync emails from Outlook.</p>
        </div>
      ) : (
        <div className="bg-white rounded shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input type="checkbox" checked={selectedIds.size === emails.length && emails.length > 0} onChange={toggleSelectAll} />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/5">{activeTab === 'sent' ? 'To' : 'From'}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell w-24">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell w-36">Received</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-40">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {emails.map(email => (
                <React.Fragment key={email.id}>
                  <tr
                    className={`hover:bg-gray-50 cursor-pointer ${
                      activeTab === 'inbox' && !email.is_read ? 'font-semibold bg-blue-50' : ''
                    }`}
                    onClick={() => handleViewEmail(email)}
                  >
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(email.id)} onChange={() => toggleSelect(email.id)} />
                    </td>
                    <td className="px-4 py-3">
                      {activeTab === 'sent' && email.to_recipients?.length > 0 ? (
                        <>
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[120px] sm:max-w-none">
                            {email.to_recipients.map(r => r.name || r.email).join(', ')}
                          </div>
                          <div className="text-xs text-gray-500 truncate max-w-[120px] sm:max-w-none">
                            {email.to_recipients.map(r => r.email).join(', ')}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[120px] sm:max-w-none">{email.sender_name || email.sender_email}</div>
                          {email.sender_name && <div className="text-xs text-gray-500 truncate max-w-[120px] sm:max-w-none">{email.sender_email}</div>}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 truncate">{email.subject}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">{email.body_preview}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {email.linked_case ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" title={email.linked_case_title || ''}>
                          Linked
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Unlinked
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap hidden md:table-cell">
                      {email.received_at ? new Date(email.received_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        {email.linked_case ? (
                          <button
                            onClick={() => handleUnlink(email.id)}
                            className="px-3 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200"
                          >
                            Unlink
                          </button>
                        ) : (
                          <button
                            onClick={() => openLinkPanel(email.id)}
                            className="px-3 py-1 bg-primary text-white text-xs rounded hover:bg-primary-dark"
                          >
                            Link
                          </button>
                        )}
                        <button
                          onClick={() => handleDismiss(email.id)}
                          className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200"
                        >
                          Dismiss
                        </button>
                      </div>
                    </td>
                  </tr>
                  {linkingId === email.id && (
                    <tr className="bg-blue-50">
                      <td colSpan="6" className="px-4 py-3">
                        {suggestions.length > 0 && (
                          <div className="mb-3">
                            <label className="text-xs font-medium text-blue-700 block mb-1">Suggested Cases:</label>
                            <div className="flex flex-wrap gap-2">
                              {suggestions.map(s => (
                                <button
                                  key={s.id}
                                  onClick={() => setLinkCaseId(String(s.id))}
                                  className={`px-3 py-1 text-xs rounded border ${String(s.id) === linkCaseId ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}
                                  title={s.reason}
                                >
                                  {s.case_number} — {s.title}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                          <label className="text-sm font-medium text-gray-700">Select Case:</label>
                          <select
                            value={linkCaseId}
                            onChange={(e) => setLinkCaseId(e.target.value)}
                            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full sm:flex-1 sm:max-w-md"
                          >
                            <option value="">— Choose a case —</option>
                            {cases.map(c => (
                              <option key={c.id} value={c.id}>
                                {c.case_number} — {c.title}
                              </option>
                            ))}
                          </select>
                          <div className="flex gap-2">
                            <button onClick={() => handleLink(email.id)} className="px-4 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700">
                              Confirm
                            </button>
                            <button onClick={() => setLinkingId(null)} className="px-4 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300">
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Email Reading Modal */}
      {viewingEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-800 truncate pr-4">{viewingEmail.subject || '(No Subject)'}</h3>
              <button onClick={() => setViewingEmail(null)} className="text-gray-400 hover:text-gray-600 text-xl flex-shrink-0">&times;</button>
            </div>
            <div className="px-6 py-4 border-b border-gray-100 text-sm text-gray-600 flex-shrink-0 space-y-1">
              <div><span className="font-medium text-gray-800">From:</span> {viewingEmail.sender_name ? `${viewingEmail.sender_name} <${viewingEmail.sender_email}>` : viewingEmail.sender_email}</div>
              {viewingEmail.to_recipients?.length > 0 && (
                <div><span className="font-medium text-gray-800">To:</span> {viewingEmail.to_recipients.map(r => r.name ? `${r.name} <${r.email}>` : r.email).join(', ')}</div>
              )}
              {viewingEmail.cc_recipients?.length > 0 && (
                <div><span className="font-medium text-gray-800">CC:</span> {viewingEmail.cc_recipients.map(r => r.name ? `${r.name} <${r.email}>` : r.email).join(', ')}</div>
              )}
              <div><span className="font-medium text-gray-800">Date:</span> {viewingEmail.received_at ? new Date(viewingEmail.received_at).toLocaleString() : '—'}</div>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
              {isLoadingDetail ? (
                <div className="text-gray-500 text-center py-8">Loading email content...</div>
              ) : viewingEmail.body_html ? (
                viewingEmail.body_type === 'html' ? (
                  <div
                    className="prose prose-sm max-w-none break-words"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewingEmail.body_html, { ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img', 'div', 'span', 'hr'], ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'style', 'target'], ADD_ATTR: ['target'] }) }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">{viewingEmail.body_html}</pre>
                )
              ) : (
                <p className="text-gray-500 italic">No email body available.</p>
              )}
            </div>
            {/* Attachments section */}
            {viewingEmail.has_attachments && viewingEmail.attachments?.length > 0 && (
              <div className="px-6 py-3 border-t border-gray-100 flex-shrink-0">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Attachments ({viewingEmail.attachments.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {viewingEmail.attachments.map((att, idx) => (
                    <a
                      key={idx}
                      href={att.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-sm text-gray-700 rounded border border-gray-200 hover:bg-gray-200"
                      title={att.name}
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                      <span className="truncate max-w-[150px]">{att.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-between items-center px-6 py-3 border-t border-gray-200 flex-shrink-0">
              <div className="flex gap-2">
                {viewingEmail.linked_case ? (
                  <button
                    onClick={() => { handleUnlink(viewingEmail.id); setViewingEmail(null); }}
                    className="px-4 py-2 bg-orange-100 text-orange-700 text-sm rounded hover:bg-orange-200"
                  >
                    Unlink from Case
                  </button>
                ) : (
                  <button
                    onClick={() => { openLinkPanel(viewingEmail.id); setViewingEmail(null); }}
                    className="px-4 py-2 bg-primary text-white text-sm rounded hover:bg-primary-dark"
                  >
                    Link to Case
                  </button>
                )}
                <button
                  onClick={() => { handleDismiss(viewingEmail.id); setViewingEmail(null); }}
                  className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded hover:bg-gray-200"
                >
                  Dismiss
                </button>
              </div>
              <button
                onClick={() => setViewingEmail(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compose Email Modal */}
      {showCompose && (
        <ComposeEmail
          authAxios={authAxios}
          showFormMessage={showFormMessage}
          onClose={() => setShowCompose(false)}
        />
      )}
    </div>
  );
}

export default UnlinkedInbox;
